import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  ScrollText, Search, RefreshCw, LogIn, LogOut, Key,
  UserPlus, UserMinus, Building2, FileText, Shield, Download,
  ChevronLeft, ChevronRight, Loader2, AlertTriangle, Ban, Globe, Mail, CheckCircle, XCircle, Bell
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const actionConfig = {
  login:                { icon: LogIn,    color: 'text-green-600',  bg: 'bg-green-50',  label: 'Connexion' },
  logout:               { icon: LogOut,   color: 'text-slate-600',  bg: 'bg-slate-100', label: 'Déconnexion' },
  login_failed:         { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Échec connexion' },
  password_change:      { icon: Key,      color: 'text-amber-600',  bg: 'bg-amber-50',  label: 'Changement MDP' },
  '2fa_enabled':        { icon: Shield,   color: 'text-green-600',  bg: 'bg-green-50',  label: '2FA activé' },
  '2fa_disabled':       { icon: Shield,   color: 'text-red-600',    bg: 'bg-red-50',    label: '2FA désactivé' },
  user_created:         { icon: UserPlus, color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Utilisateur créé' },
  user_updated:         { icon: UserPlus, color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Utilisateur modifié' },
  user_deleted:         { icon: UserMinus,color: 'text-red-600',    bg: 'bg-red-50',    label: 'Utilisateur supprimé' },
  employee_created:     { icon: UserPlus, color: 'text-green-600',  bg: 'bg-green-50',  label: 'Employé créé' },
  employee_updated:     { icon: UserPlus, color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Employé modifié' },
  employee_deleted:     { icon: UserMinus,color: 'text-red-600',    bg: 'bg-red-50',    label: 'Employé supprimé' },
  company_created:      { icon: Building2,color: 'text-green-600',  bg: 'bg-green-50',  label: 'Entreprise créée' },
  company_updated:      { icon: Building2,color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Entreprise modifiée' },
  company_deleted:      { icon: Building2,color: 'text-red-600',    bg: 'bg-red-50',    label: 'Entreprise supprimée' },
  leave_approved:       { icon: FileText, color: 'text-green-600',  bg: 'bg-green-50',  label: 'Congé approuvé' },
  leave_rejected:       { icon: FileText, color: 'text-red-600',    bg: 'bg-red-50',    label: 'Congé rejeté' },
  payroll_generated:    { icon: FileText, color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Paie générée' },
  device_created:       { icon: Shield,   color: 'text-green-600',  bg: 'bg-green-50',  label: 'Pointeur créé' },
  justification_reviewed:{ icon: FileText,color: 'text-amber-600', bg: 'bg-amber-50',  label: 'Justificatif revu' },
  data_exported:        { icon: Download, color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Export données' },
};

const actionTypes = [
  { value: '', label: 'Toutes les actions' },
  { value: 'login', label: 'Connexions' },
  { value: 'login_failed', label: 'Échecs connexion' },
  { value: 'password_change', label: 'Changements MDP' },
  { value: '2fa_enabled', label: '2FA activé' },
  { value: '2fa_disabled', label: '2FA désactivé' },
  { value: 'employee_created', label: 'Employés créés' },
  { value: 'leave_approved', label: 'Congés approuvés' },
  { value: 'leave_rejected', label: 'Congés rejetés' },
  { value: 'payroll_generated', label: 'Paies générées' },
];

export default function AuditLogs() {
  const { token, user } = useAuth();
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [secStats, setSecStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' | 'security'
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const logsPerPage = 20;

  useEffect(() => { fetchLogs(); }, [actionFilter]);
  useEffect(() => {
    if (activeTab === 'security') {
      fetchSecStats();
      fetchAlerts();
    }
  }, [activeTab]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/audit-logs?limit=500`;
      if (actionFilter) url += `&action=${actionFilter}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/security/failed-logins-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecStats(response.data);
    } catch (error) {
      console.error('Failed to fetch security stats', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/security/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/security/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_acknowledged: true } : a));
    } catch (error) {
      console.error('Failed to acknowledge alert', error);
    }
  };

  const scopedLogs = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return logs;
    return logs.filter(log => log.company_id === selectedCompanyId);
  }, [logs, selectedCompanyId, isCrossCompany]);

  const filteredLogs = scopedLogs.filter(log =>
    log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.ip_address?.includes(searchTerm)
  );

  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
  const paginatedLogs = filteredLogs.slice((page - 1) * logsPerPage, page * logsPerPage);

  const formatDate = (dateStr) => new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium', timeStyle: 'short'
  }).format(new Date(dateStr));

  const formatRemaining = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}min ${s}s` : `${s}s`;
  };

  const getActionDisplay = (action) => {
    const config = actionConfig[action] || { icon: ScrollText, color: 'text-slate-600', bg: 'bg-slate-100', label: action };
    const Icon = config.icon;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${config.bg}`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
        <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
      </div>
    );
  };

  if (!['super_admin', 'company_admin', 'group_admin', 'group_hr'].includes(user?.role)) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-700">Vous n'avez pas les permissions pour accéder à cette page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-primary" />
            Journal d'audit
          </h1>
          <p className="text-slate-600">Historique des actions et surveillance de la sécurité</p>
        </div>
        <Button variant="outline" onClick={activeTab === 'logs' ? fetchLogs : fetchSecStats} disabled={loading || statsLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${(loading || statsLoading) ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'logs' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
        >
          <ScrollText className="w-4 h-4 inline mr-2" />
          Journal d'audit
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'security' ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Sécurité — Connexions échouées
        </button>
      </div>

      {/* ── ONGLET JOURNAL ── */}
      {activeTab === 'logs' && (
        <>
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par email, IP, détails..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    className="pl-10"
                    data-testid="search-logs"
                  />
                </div>
                <div className="sm:w-48">
                  <select
                    value={actionFilter}
                    onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="action-filter"
                  >
                    {actionTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {filteredLogs.length} entrée{filteredLogs.length > 1 ? 's' : ''} trouvée{filteredLogs.length > 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ScrollText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun log trouvé</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Action</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Utilisateur</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Détails</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLogs.map((log) => (
                          <tr key={log.id} className={`border-b border-slate-100 hover:bg-slate-50 ${log.action === 'login_failed' ? 'bg-red-50/40' : ''}`}>
                            <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">{formatDate(log.created_at)}</td>
                            <td className="py-3 px-4">{getActionDisplay(log.action)}</td>
                            <td className="py-3 px-4 text-sm text-slate-900">{log.user_email}</td>
                            <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">{log.details || '-'}</td>
                            <td className="py-3 px-4 text-sm text-slate-500 font-mono">{log.ip_address || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-600">Page {page} sur {totalPages}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── ONGLET SÉCURITÉ ── */}
      {activeTab === 'security' && (
        <>
          {statsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : secStats ? (
            <div className="space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-5">
                    <p className="text-xs text-red-500 font-medium uppercase tracking-wide">Échecs 24h</p>
                    <p className="text-3xl font-bold text-red-700 mt-1">{secStats.summary.failures_24h}</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="pt-5">
                    <p className="text-xs text-orange-500 font-medium uppercase tracking-wide">Échecs 7 jours</p>
                    <p className="text-3xl font-bold text-orange-700 mt-1">{secStats.summary.failures_7d}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Total historique</p>
                    <p className="text-3xl font-bold text-slate-700 mt-1">{secStats.summary.failures_total}</p>
                  </CardContent>
                </Card>
                <Card className={secStats.summary.blocked_ips_count > 0 ? 'border-red-400 bg-red-50' : 'border-green-200 bg-green-50'}>
                  <CardContent className="pt-5">
                    <p className={`text-xs font-medium uppercase tracking-wide ${secStats.summary.blocked_ips_count > 0 ? 'text-red-500' : 'text-green-500'}`}>IPs bloquées</p>
                    <p className={`text-3xl font-bold mt-1 ${secStats.summary.blocked_ips_count > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {secStats.summary.blocked_ips_count}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* IPs bloquées */}
              {secStats.blocked_ips.length > 0 && (
                <Card className="border-red-300">
                  <CardHeader>
                    <CardTitle className="text-base text-red-700 flex items-center gap-2">
                      <Ban className="w-5 h-5" />
                      IPs actuellement bloquées par le rate limiter
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {secStats.blocked_ips.map((b, i) => (
                        <div key={i} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                          <span className="font-mono text-sm font-bold text-red-800">{b.ip || '—'}</span>
                          <div className="flex items-center gap-4 text-sm text-red-600">
                            <span>{b.total_failures} tentatives</span>
                            <span className="bg-red-200 text-red-800 px-2 py-0.5 rounded font-medium">
                              Bloquée {formatRemaining(b.remaining_seconds)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Top IPs attaquantes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="w-5 h-5 text-slate-500" />
                      Top IPs attaquantes (7 jours)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {secStats.top_attacker_ips.length === 0 ? (
                      <p className="text-sm text-green-600 font-medium">✅ Aucune attaque détectée</p>
                    ) : (
                      <div className="space-y-2">
                        {secStats.top_attacker_ips.map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <span className="font-mono text-sm text-slate-800">{item.ip || '—'}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-500">{item.last_attempt ? formatDate(item.last_attempt) : ''}</span>
                              <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">{item.count} échecs</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top emails ciblés */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="w-5 h-5 text-slate-500" />
                      Comptes les plus ciblés (7 jours)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {secStats.top_targeted_emails.length === 0 ? (
                      <p className="text-sm text-green-600 font-medium">✅ Aucune tentative détectée</p>
                    ) : (
                      <div className="space-y-2">
                        {secStats.top_targeted_emails.map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-800 truncate max-w-[200px]">{item.email}</span>
                            <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded">{item.count} tentatives</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Dernières tentatives échouées */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Dernières tentatives échouées (7 jours)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {secStats.recent_failures.length === 0 ? (
                    <p className="text-sm text-green-600 font-medium">✅ Aucun échec de connexion récent</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">IP</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Raison</th>
                          </tr>
                        </thead>
                        <tbody>
                          {secStats.recent_failures.map((f, i) => (
                            <tr key={i} className="border-b border-slate-100 hover:bg-red-50/30">
                              <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(f.created_at)}</td>
                              <td className="py-2 px-3 text-slate-800">{f.user_email}</td>
                              <td className="py-2 px-3 font-mono text-slate-500">{f.ip_address || '—'}</td>
                              <td className="py-2 px-3 text-slate-600">{f.details || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── Alertes IDS ── */}
              <Card className={alerts.filter(a => !a.is_acknowledged).length > 0 ? 'border-red-400' : ''}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="w-5 h-5 text-red-500" />
                    Alertes de détection d'intrusion
                    {alerts.filter(a => !a.is_acknowledged).length > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {alerts.filter(a => !a.is_acknowledged).length}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {alertsLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : alerts.length === 0 ? (
                    <p className="text-sm text-green-600 font-medium">✅ Aucune alerte d'intrusion détectée</p>
                  ) : (
                    <div className="space-y-3">
                      {alerts.map((alert) => (
                        <div
                          key={alert.id}
                          className={`rounded-lg border p-4 flex items-start justify-between gap-4 ${
                            alert.is_acknowledged
                              ? 'bg-slate-50 border-slate-200 opacity-60'
                              : alert.severity === 'critical'
                              ? 'bg-red-50 border-red-300'
                              : 'bg-amber-50 border-amber-300'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                alert.severity === 'critical' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                              }`}>
                                {alert.severity === 'critical' ? '🚨 CRITIQUE' : '⚠️ ÉLEVÉ'}
                              </span>
                              <span className="text-xs text-slate-500 font-mono">{alert.alert_type}</span>
                              {alert.occurrence_count > 1 && (
                                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                                  ×{alert.occurrence_count}
                                </span>
                              )}
                            </div>
                            <p className="font-semibold text-slate-900 mt-1 text-sm">{alert.title}</p>
                            <p className="text-slate-600 text-xs mt-0.5">{alert.description}</p>
                            <p className="text-slate-400 text-xs mt-1">{formatDate(alert.created_at)}</p>
                            {alert.is_acknowledged && (
                              <p className="text-slate-400 text-xs mt-1">✓ Acquittée par {alert.acknowledged_by}</p>
                            )}
                          </div>
                          {!alert.is_acknowledged && (
                            <button
                              onClick={() => handleAcknowledge(alert.id)}
                              className="shrink-0 flex items-center gap-1 text-xs bg-white border border-slate-300 text-slate-700 hover:bg-green-50 hover:border-green-400 hover:text-green-700 px-3 py-1.5 rounded-md transition-colors"
                              title="Marquer comme traitée"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Acquitter
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune donnée disponible</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
